import { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithCorpId } from '../utils/api';
import * as XLSX from 'xlsx';

const SortIcon = ({ col, sortConfig }) => {
  if (sortConfig.key !== col) return <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: 0.35, marginLeft: 4 }}><path d="M12 5v14M5 12l7-7 7 7"/></svg>;
  return sortConfig.direction === 'asc'
    ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" style={{ marginLeft: 4 }}><path d="M12 19V5M5 12l7-7 7 7"/></svg>
    : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" style={{ marginLeft: 4 }}><path d="M12 5v14M5 12l7 7 7-7"/></svg>;
};

const StatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase();
  const cls = s === 'paid' ? 'badge badge-success' : s === 'pending' ? 'badge badge-warning' : s === 'overdue' ? 'badge badge-danger' : 'badge badge-neutral';
  return <span className={cls}>{status}</span>;
};

function InvoiceTable({ onEdit }) {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [filter, setFilter] = useState({ number: '', customer: '', status: '' });
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const menuRefs = useRef({});
  const statusOptions = useMemo(() => {
    const set = new Set(); (invoices || []).forEach(inv => { if (inv?.status) set.add(inv.status); });
    return Array.from(set).sort();
  }, [invoices]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchInvoices = async () => {
    try {
      const data = await fetchWithCorpId(`/invoices?page=${page}&size=${size}`);
      if (Array.isArray(data)) {
        setInvoices(data); setFilteredInvoices(data); setTotalElements(data.length); setTotalPages(1);
      } else {
        const { content = [], totalElements: total = 0, totalPages: totalP = 0 } = data || {};
        setInvoices(content); setFilteredInvoices(content);
        setTotalElements(total); setTotalPages(totalP || (content.length ? 1 : 0));
      }
    } catch { setError('Failed to fetch invoices'); } finally { setLoading(false); }
  };

  const notify = (msg, isErr = false) => {
    if (isErr) { setError(msg); setTimeout(() => setError(null), 5000); }
    else { setMessage(msg); setTimeout(() => setMessage(null), 5000); }
  };

  const handleDownloadPDF = async (id) => {
    try {
      const res = await fetchWithCorpId(`/invoices/${id}/pdf-db`, { method: 'GET', headers: { Accept: 'application/pdf' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      if (blob.type !== 'application/pdf') throw new Error('Invalid response type');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `invoice-${id}.pdf`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
      notify('PDF downloaded successfully.');
    } catch (e) { notify(`Failed to download PDF: ${e.message}`, true); }
  };

  const handleViewHTML = async (id) => {
    try {
      const res = await fetchWithCorpId(`/invoices/${id}/html-view`, { method: 'GET', headers: { Accept: 'text/html' } });
      const w = window.open('', '_blank'); w.document.write(res); w.document.close();
      notify('HTML view opened in new window.');
    } catch (e) { notify(`Failed to view: ${e.message}`, true); }
  };

  const handleSendReminder = async (id) => {
    try { await fetchWithCorpId(`/invoices/${id}/remind`, { method: 'PUT' }); await fetchInvoices(); notify('Reminder sent.'); }
    catch { notify('Failed to send reminder.', true); }
  };

  const handleMarkAsPaid = async (id) => {
    try { await fetchWithCorpId(`/invoices/${id}/paid`, { method: 'PUT' }); await fetchInvoices(); notify('Invoice marked as paid.'); }
    catch { notify('Failed to mark as paid.', true); }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetchWithCorpId(`/invoices/${id}`, { method: 'DELETE' });
      if (res.ok) { await fetchInvoices(); notify('Invoice deleted.'); }
      else throw new Error('Failed to delete');
    } catch (e) { notify(`Failed to delete: ${e.message}`, true); }
  };

  useEffect(() => {
    fetchInvoices();
    const close = (e) => {
      if (openActionMenu) { const ref = menuRefs.current[openActionMenu]; if (ref && !ref.contains(e.target)) setOpenActionMenu(null); }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [page, size]);
  useEffect(() => { applyFilters(); }, [filter, invoices]);
  useEffect(() => { applySorting(); }, [sortConfig]);

  const applyFilters = () => {
    setFilteredInvoices(invoices.filter(inv =>
      (!filter.number || inv.number?.toString().includes(filter.number)) &&
      (!filter.customer || inv.customer?.toLowerCase().includes(filter.customer.toLowerCase())) &&
      (!filter.status || inv.status?.toLowerCase() === filter.status.toLowerCase())
    ));
  };

  const applySorting = () => {
    if (!sortConfig.key) return;
    setFilteredInvoices(prev => [...prev].sort((a, b) => {
      const vA = a[sortConfig.key], vB = b[sortConfig.key];
      if (vA == null) return 1; if (vB == null) return -1;
      if (typeof vA === 'number') return sortConfig.direction === 'asc' ? vA - vB : vB - vA;
      return sortConfig.direction === 'asc' ? vA.toString().localeCompare(vB.toString()) : vB.toString().localeCompare(vA.toString());
    }));
  };

  const handleSort = (key) => setSortConfig(p => ({ key, direction: p.key === key && p.direction === 'asc' ? 'desc' : 'asc' }));
  const handleFilterChange = (e) => setFilter(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleChangePageSize = (e) => { setSize(parseInt(e.target.value, 10)); setPage(0); };

  const handleDownloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredInvoices.map(inv => ({
      Number: inv.number, Customer: inv.customer, Amount: inv.amount,
      'Invoice Date': inv.invoiceDate, 'Due Date': inv.dueDate, Status: inv.status,
    })));
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Invoices');
    XLSX.writeFile(wb, 'invoices.xlsx');
  };

  return (
    <div className="page-container">
      <div className="table-toolbar">
        <div>
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#1e293b' }}>Invoices</h1>
          <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>Track and manage all invoices</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn-info" onClick={handleDownloadExcel}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export
          </button>
          <button className="btn-primary" onClick={() => navigate('/on/invoices/new')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Invoice
          </button>
        </div>
      </div>

      <div className="filter-bar">
        <input type="number" name="number" placeholder="Invoice #" value={filter.number} onChange={handleFilterChange} />
        <input name="customer" placeholder="Customer" value={filter.customer} onChange={handleFilterChange} />
        <select name="status" value={filter.status} onChange={handleFilterChange}>
          <option value="">All Statuses</option>
          {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button className="btn-secondary" onClick={() => setFilter({ number: '', customer: '', status: '' })}>Clear</button>
      </div>

      {message && <div className="alert success" style={{ marginBottom: '14px' }}>{message}</div>}
      {error && <div className="alert error" style={{ marginBottom: '14px' }}>{error}</div>}

      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>Loading invoices…</div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('number')}>Number <SortIcon col="number" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('customer')}>Customer <SortIcon col="customer" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('amount')}>Amount <SortIcon col="amount" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('invoiceDate')}>Invoice Date <SortIcon col="invoiceDate" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('dueDate')}>Due Date <SortIcon col="dueDate" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>Status <SortIcon col="status" sortConfig={sortConfig} /></th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.length === 0 ? (
                <tr className="state-row"><td colSpan="7">No invoices found.</td></tr>
              ) : filteredInvoices.map(inv => (
                <tr key={inv.id}>
                  <td><span className="table-link" onClick={() => navigate(`/on/invoices/edit/${inv.id}`)}>{inv.number}</span></td>
                  <td style={{ fontWeight: '500' }}>{inv.customer}</td>
                  <td style={{ fontWeight: '600', color: '#1e293b' }}>${inv.amount?.toFixed(2)}</td>
                  <td style={{ color: '#64748b', fontSize: '13px' }}>{inv.invoiceDate}</td>
                  <td style={{ color: '#64748b', fontSize: '13px' }}>{inv.dueDate}</td>
                  <td><StatusBadge status={inv.status} /></td>
                  <td style={{ position: 'relative' }} ref={el => menuRefs.current[inv.id] = el}>
                    <button
                      onClick={() => setOpenActionMenu(openActionMenu === inv.id ? null : inv.id)}
                      style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '5px 10px', cursor: 'pointer', color: '#64748b', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></svg>
                    </button>
                    {openActionMenu === inv.id && (
                      <div className="action-menu">
                        <div className="action-menu-item" onClick={() => { setOpenActionMenu(null); handleViewHTML(inv.id); }}>View</div>
                        <div className="action-menu-item" onClick={() => { setOpenActionMenu(null); handleDownloadPDF(inv.id); }}>Download PDF</div>
                        {inv.status === 'Pending' && <>
                          <div className="action-menu-item" onClick={() => { setOpenActionMenu(null); handleSendReminder(inv.id); }}>{inv.sent ? 'Remind' : 'Send'}</div>
                          <div className="action-menu-item" onClick={() => { setOpenActionMenu(null); handleMarkAsPaid(inv.id); }}>Mark as Paid</div>
                        </>}
                        <div className="action-menu-item danger" onClick={() => { setOpenActionMenu(null); handleDelete(inv.id); }}>Delete</div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination">
            <span style={{ fontSize: '13px', color: '#64748b', marginRight: 'auto' }}>
              {typeof totalElements === 'number' ? `${totalElements} total` : ''}
            </span>
            <label style={{ fontSize: '13px', color: '#64748b' }}>Rows:&nbsp;
              <select value={size} onChange={handleChangePageSize} style={{ fontSize: '13px', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '3px 6px' }}>
                {[5, 10, 25, 50, 100].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </label>
            <span style={{ fontSize: '13px', color: '#64748b' }}>Page {totalPages > 0 ? page + 1 : 0} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>← Prev</button>
            <button onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={totalPages === 0 || page >= totalPages - 1}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default InvoiceTable;
